/** @jsxImportSource @emotion/react */

import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { createHotkeys } from 'shared/utils';
import { ClickableArea } from 'ud-ui/components';
import EmptyButton from 'ud-ui/components/empty-button';
import CloseIcon from 'ud-ui/components/icon/close-icon';

import Portal from '../portal/portal';
import { isTopModal, pushModal, removeModal } from './modal-stack';
import * as S from './styles';

export type UDModalProps = {
  children: React.ReactNode | React.ReactNode[];

  show: boolean;
  onClose?: () => void;
  noOverflow?: boolean;

  disableEscapeKey?: boolean;
  disableScrollLock?: boolean;
  disableOverlayClick?: boolean;
};

const UDModal = (props: UDModalProps) => {
  const nodeRef = useRef<HTMLDivElement>(null);
  const modalId = useMemo(() => Symbol('modal'), []);

  // Escape закрывает только верхнюю модалку. Обработчики всех открытых модалок
  // висят на одном `window`, поэтому «съесть» событие соседу нельзя — вместо
  // этого нижние отсеиваются по стеку (`modal-stack.ts`).
  //
  // Слушатель живёт в эффекте, а не в теле компонента: `createHotkeys` вешает
  // его на `window` сразу при вызове, и без `dispose` каждый рендер добавлял бы
  // ещё один, а закрытая модалка продолжала бы слушать Escape.
  const onCloseRef = useRef(props.onClose);
  onCloseRef.current = props.onClose;

  useEffect(() => {
    if (!props.show || props.disableEscapeKey) return;

    pushModal(modalId);
    const hotkeys = createHotkeys([
      {
        combo: ['Escape'],
        when: () => isTopModal(modalId),
        handler: () => onCloseRef.current?.(),
      },
    ]);

    return () => {
      hotkeys.dispose();
      removeModal(modalId);
    };
  }, [modalId, props.disableEscapeKey, props.show]);

  // Блокировка скролла body
  useEffect(() => {
    if (!props.show || props.disableScrollLock) return;

    const originalStyle = window.getComputedStyle(document.body).overflow;
    document.body.style.overflow = 'hidden';

    return () => {
      document.body.style.overflow = originalStyle;
    };
  }, [props.disableScrollLock, props.show]);

  // Focus trap
  useEffect(() => {
    if (!props.show || !nodeRef.current) return;

    const modalElement = nodeRef.current;
    const focusableElements = modalElement.querySelectorAll<HTMLElement>(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
    );
    const firstElement = focusableElements[0];
    const lastElement = focusableElements[focusableElements.length - 1];

    const handleTab = (event: KeyboardEvent) => {
      if (event.key !== 'Tab') return;

      if (event.shiftKey) {
        if (document.activeElement === firstElement) {
          event.preventDefault();
          lastElement?.focus();
        }
      } else {
        if (document.activeElement === lastElement) {
          event.preventDefault();
          firstElement?.focus();
        }
      }
    };

    // Устанавливаем фокус на первый элемент при открытии
    firstElement?.focus();

    modalElement.addEventListener('keydown', handleTab as any);
    return () => modalElement.removeEventListener('keydown', handleTab as any);
  }, [props.show]);

  const handleOverlayClick = useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      if (props.disableOverlayClick) return;

      // Проверяем, что клик был именно по оверлею, а не по контенту
      if (event.target === event.currentTarget && props.onClose) {
        props.onClose();
      }
    },
    [props],
  );

  if (!props.show) {
    return null;
  }

  return (
    <Portal wrapperId="portal_modal_container">
      <S.MainBlock className="flex items-center justify-center" onClick={handleOverlayClick} role="dialog" aria-modal="true">
        <S.ModalContainer className="px-6 pt-8.5 pb-10" ref={nodeRef} style={{ overflow: props.noOverflow ? 'unset' : 'auto' }}>
          <div className="close">
            <EmptyButton
              onClick={props.onClose}
              icon={
                <ClickableArea horizontalExtend={10} verticalExtend={10}>
                  <CloseIcon />
                </ClickableArea>
              }
            />
          </div>
          {props.children}
        </S.ModalContainer>
      </S.MainBlock>
    </Portal>
  );
};

export default UDModal;
